import logger from '@/app/logger';
import mitt from 'mitt';
import type { Handler } from 'mitt';
import type { ComponentPublicInstance } from 'vue';

const emitter = mitt();

// Listeners should be component specific, which allows unsubscribing for one component only
const componentListeners = new Map<ComponentPublicInstance, { event: string; handler: Handler<any> }[]>();

export interface EventBus {
  $on(event: string, handler: Handler<any>, listenerScope?: ComponentPublicInstance | null): void;
  $off(event: string, component: ComponentPublicInstance | null): void;
  $emit(event: string, arg?: any): void;
}

/**
 * EventBus is a simple event bus implementation using mitt.
 * @deprecated Replace global event based functionality with a more native and simpler approach.
 * Vue's guidelines for replacing global events:
 * - Props and events should be your first choice for parent-child communication. Siblings can communicate via their parent.
 * - Provide / inject allow a component to communicate with its slot contents. This is useful for tightly-coupled components that are always used together.
 * - Provide / inject can also be used for long-distance communication between components. It can help to avoid 'prop drilling', where props need to be passed down through many levels of components that don't need those props themselves.
 * - Prop drilling can also be avoided by refactoring to use slots. If an interim component doesn't need the props then it might indicate a problem with separation of concerns. Introducing a slot in that component allows the parent to create the content directly, so that props can be passed without the interim component needing to get involved.
 * - Global state management, such as Pinia.
 */
export default {
  /**
   * @deprecated Replace global event based functionality with a more native and simpler approach
   */
  $on(event: string, handler: Handler<any>, listenerScope?: ComponentPublicInstance | null) {
    emitter.on(event, handler);

    if (listenerScope) {
      if (!componentListeners.has(listenerScope)) {
        componentListeners.set(listenerScope, []);
      }
      componentListeners.get(listenerScope)?.push({ event, handler });
    } else {
      logger.warn(
        `EventBus on: ${event} - No component provided, thus unsubscribing fill impact all subscriptions regardless of component`
      );
    }
  },
  /**
   * @deprecated Replace global event based functionality with a more native and simpler approach
   */
  $off(event: string, listenerScope?: ComponentPublicInstance | null) {
    if (listenerScope) {
      const listeners = componentListeners.get(listenerScope) || [];
      listeners.forEach(({ event: registeredEvent, handler }) => {
        if (registeredEvent === event) {
          emitter.off(event, handler);
        }
      });

      componentListeners.set(
        listenerScope,
        listeners.filter(({ event: registeredEvent }) => registeredEvent !== event)
      );
    } else {
      logger.warn(`EventBus off: ${event} - No component provided, removing ${event} listeners for all components`);
      emitter.off(event);
    }
  },
  /**
   * @deprecated Replace global event based functionality with a more native and simpler approach
   */
  $emit(event: string, arg?: any) {
    emitter.emit(event, arg);
  },
} satisfies EventBus;
